import React, { useEffect, useRef, useState } from 'react';
import { CharacterSheet } from '../../types/CharacterSheet';
import { ProfileManager } from '../ProfileManager';
import { Form } from '../Form';
import { RoomPlayer } from '../RoomPlayer';
import { DiceRoller } from '../DiceRoller';
import { Modal } from '../Modal';
import { ConfirmationModal } from '../ConfirmationModal';
import { Toast } from '../Toast';
import { Rules } from '../Rules';
import { IoAlertCircleOutline, IoBookOutline, IoChevronBackOutline, IoChevronDownOutline, IoChevronForwardOutline, IoChevronUpOutline, IoCopyOutline, IoCreateOutline, IoDiceOutline, IoEyeOutline, IoFlashOffOutline, IoFlashOutline, IoMoonOutline, IoPeopleOutline, IoSettingsOutline, IoSwapHorizontalOutline, IoWarningOutline } from 'react-icons/io5';
import { Logo } from '../Logo';
import { encodeRoomId } from '../../utils/rooms';
import { completeGmNameStep, completeGmShareStep, getGmFlowState, setStoredGmName } from '../../utils/gmFlow';
import { getRoomMeta, updateDiceConfig, updateRoomTheme } from '../../utils/roomClient';
import { ROOM_THEME_OPTIONS, RoomTheme } from '../../types/RoomTheme';
import { DEFAULT_DICE_CONFIG, DiceConfig, RoomMode } from '../../types/RoomMode';
import './styles.css';

interface Props {
    roomId: string;
    players: Record<string, any>;
    playerId: string;
    formData: CharacterSheet;
    activeProfileId: string | null;
    showProfileManager: boolean;
    onFormChange: (data: CharacterSheet) => void;
    onSelectProfile: (id: string) => void;
    onToggleProfileManager: (show: boolean) => void;
    onLeaveRoom: () => void;
    roomTheme?: RoomTheme;
    onRoomThemeChange?: (theme: RoomTheme) => void;
}

function isMobileViewport() {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 56rem)').matches;
}

function countSuccesses(chains: number[][], difficulty?: number | null) {
    if (typeof difficulty !== 'number') return 0;
    return chains.reduce(
        (acc, chain) => acc + chain.reduce((inner, roll) => inner + (roll >= difficulty ? 1 : 0), 0),
        0
    );
}

function getSheetPreview(sheet?: CharacterSheet | null): Required<CharacterSheet> {
    return {
        name: sheet?.name ?? '',
        occupation: sheet?.occupation ?? '',
        description: sheet?.description ?? '',
        shadow: sheet?.shadow ?? '0',
        agility: sheet?.agility ?? '',
        strength: sheet?.strength ?? '',
        dexterity: sheet?.dexterity ?? '',
        culture: sheet?.culture ?? '',
        craft: sheet?.craft ?? '',
        temperance: sheet?.temperance ?? '',
        insight: sheet?.insight ?? '',
        brio: sheet?.brio ?? '3',
        stress: sheet?.stress ?? '0',
        notes: sheet?.notes ?? '',
        consequences: Array.isArray(sheet?.consequences) ? sheet.consequences : [],
    };
}

const CONSEQUENCE_TYPE_LABELS: Record<'leve' | 'moderada' | 'grave' | 'crítica', string> = {
    leve: '-1d durante 1 hora',
    moderada: '-1d hasta descanso',
    grave: '-2d hasta tratamiento',
    crítica: 'Incapacitado',
};

const RoomWorkspace: React.FC<Props> = ({
    roomId,
    players,
    playerId,
    formData,
    activeProfileId,
    showProfileManager,
    onFormChange,
    onSelectProfile,
    onToggleProfileManager,
    onLeaveRoom,
    roomTheme = 'brio',
    onRoomThemeChange = () => {},
}) => {
    const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
    const [switchConfirmOpen, setSwitchConfirmOpen] = useState(false);
    const [switchModalOpen, setSwitchModalOpen] = useState(false);
    const [rulesModalOpen, setRulesModalOpen] = useState(false);
    const [roomMenuOpen, setRoomMenuOpen] = useState(false);
    const [mobileRoomPanelOpen, setMobileRoomPanelOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [isGm, setIsGm] = useState(false);
    const [showGmNameModal, setShowGmNameModal] = useState(false);
    const [requireGmNameStep, setRequireGmNameStep] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [requireShareStep, setRequireShareStep] = useState(false);
    const [gmNameDraft, setGmNameDraft] = useState('');
    const [gmNameError, setGmNameError] = useState('');
    const [themeModalOpen, setThemeModalOpen] = useState(false);
    const [themeDraft, setThemeDraft] = useState<RoomTheme>(roomTheme);
    const [themeError, setThemeError] = useState('');
    const [roomMode, setRoomMode] = useState<RoomMode>('brio');
    const [roomDiceConfig, setRoomDiceConfig] = useState<DiceConfig>(DEFAULT_DICE_CONFIG);
    const [diceConfigDraft, setDiceConfigDraft] = useState<DiceConfig>(DEFAULT_DICE_CONFIG);
    const [diceConfigError, setDiceConfigError] = useState('');
    const [onlyDiceNameDraft, setOnlyDiceNameDraft] = useState('');
    const [isEditingName, setIsEditingName] = useState(false);
    const originalThemeRef = useRef<RoomTheme>(roomTheme);
    const [inspectedPlayerId, setInspectedPlayerId] = useState<string | null>(null);
    const [updatedRollPlayers, setUpdatedRollPlayers] = useState<Record<string, boolean>>({});
    const hasHydratedRollBaseline = useRef(false);
    const roomSessionStartedAt = useRef(Date.now());
    const prevRollByPlayer = useRef<Record<string, number>>({});
    const rollAnimationTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const roomMenuRef = useRef<HTMLDivElement | null>(null);
    const inviteUrl = `${globalThis.location?.origin ?? ''}/sala/${encodeRoomId(roomId)}`;
    const currentDisplayName = players[playerId]?.name?.trim() || formData.name?.trim() || 'Anónimo';
    const otherPlayers = Object.entries(players).filter(([id]) => id !== playerId);
    const inspectedPlayer = inspectedPlayerId ? players[inspectedPlayerId] : null;
    const inspectedSheetPreview = getSheetPreview(inspectedPlayer?.characterSheet);

    useEffect(() => {
        setThemeDraft(roomTheme);
    }, [roomTheme]);

    useEffect(() => {
        if (!isMobileViewport()) {
            setMobileRoomPanelOpen(true);
            return;
        }
        setMobileRoomPanelOpen(false);
    }, [roomId]);

    useEffect(() => {
        hasHydratedRollBaseline.current = false;
        roomSessionStartedAt.current = Date.now();
        prevRollByPlayer.current = {};
        setUpdatedRollPlayers({});
    }, [roomId]);

    useEffect(() => {
        return () => {
            Object.values(rollAnimationTimers.current).forEach(timer => clearTimeout(timer));
            rollAnimationTimers.current = {};
        };
    }, []);

    useEffect(() => {
        const onOutsideClick = (event: MouseEvent) => {
            if (!roomMenuRef.current) return;
            if (roomMenuRef.current.contains(event.target as Node)) return;
            setRoomMenuOpen(false);
        };
        if (roomMenuOpen) {
            document.addEventListener('mousedown', onOutsideClick);
        }
        return () => document.removeEventListener('mousedown', onOutsideClick);
    }, [roomMenuOpen]);

    useEffect(() => {
        let mounted = true;
        setIsGm(false);
        setShowGmNameModal(false);
        setRequireGmNameStep(false);
        setShowShareModal(false);
        setRequireShareStep(false);
        setInspectedPlayerId(null);

        void getRoomMeta(roomId).then(meta => {
            if (!mounted) return;
            const currentIsGm = Boolean(meta && meta.gmId === playerId);
            setIsGm(currentIsGm);
            setRoomMode(meta?.mode ?? 'brio');
            const cfg = meta?.diceConfig ?? DEFAULT_DICE_CONFIG;
            setRoomDiceConfig(cfg);
            setDiceConfigDraft(cfg);
            if (!currentIsGm) return;

            const flowState = getGmFlowState(roomId, playerId);
            if (flowState.needsName) {
                setRequireGmNameStep(true);
                setShowGmNameModal(true);
                setGmNameDraft(formData.name?.trim() || '');
                return;
            }
            if (flowState.needsShare) {
                setRequireShareStep(true);
                setShowShareModal(true);
            }
        }).catch(error => {
            console.error('getRoomMeta failed', error);
        });

        return () => {
            mounted = false;
        };
    }, [roomId, playerId, formData.name]);

    useEffect(() => {
        const entries = Object.entries(players);
        const nextMap: Record<string, number> = {};
        let externalRollMessage: string | null = null;
        const newlyRolledIds: string[] = [];

        entries.forEach(([id, player]) => {
            const ts = Number(player?.lastRoll?.timestamp ?? 0);
            nextMap[id] = ts;
            if (!ts) return;
            const prevTs = prevRollByPlayer.current[id] ?? 0;
            if (ts <= prevTs) return;
            if (ts < roomSessionStartedAt.current) return;
            newlyRolledIds.push(id);
            if (rollAnimationTimers.current[id]) {
                clearTimeout(rollAnimationTimers.current[id]);
            }
            rollAnimationTimers.current[id] = setTimeout(() => {
                setUpdatedRollPlayers(current => {
                    const next = { ...current };
                    delete next[id];
                    return next;
                });
                delete rollAnimationTimers.current[id];
            }, 1100);
            if (id === playerId) return;
            if (!externalRollMessage) {
                const displayName = player?.name?.trim() || 'Anónimo';
                const rollChains = Array.isArray(player?.lastRoll?.chains) ? player.lastRoll.chains : [];
                const diceType = player?.lastRoll?.diceType ?? null;
                if (diceType) {
                    const allValues = rollChains.flat();
                    externalRollMessage = `${displayName} lanza ${diceType}: ${allValues.join(', ')}`;
                } else {
                    const successes = countSuccesses(rollChains, player?.lastRoll?.difficulty ?? null);
                    const successCopy = successes === 1 ? 'exito' : 'exitos';
                    externalRollMessage = `${displayName} obtiene ${successes} ${successCopy}`;
                }
            }
        });

        if (!hasHydratedRollBaseline.current) {
            hasHydratedRollBaseline.current = true;
            prevRollByPlayer.current = { ...prevRollByPlayer.current, ...nextMap };
            return;
        }

        // Preserve known timestamps for players that may disappear temporarily
        // from a snapshot (network jitter/reconnect), so historical rolls are
        // not re-announced when they reappear unchanged.
        prevRollByPlayer.current = { ...prevRollByPlayer.current, ...nextMap };

        if (newlyRolledIds.length) {
            setUpdatedRollPlayers(current => {
                const next = { ...current };
                newlyRolledIds.forEach(id => {
                    next[id] = true;
                });
                return next;
            });
        }

        if (externalRollMessage && !mobileRoomPanelOpen) {
            setToastMessage(externalRollMessage);
        }

        return () => {
            newlyRolledIds.forEach(id => {
                if (rollAnimationTimers.current[id]) {
                    clearTimeout(rollAnimationTimers.current[id]);
                }
            });
        };
    }, [mobileRoomPanelOpen, playerId, players]);

    const handleSelectProfileInline = (id: string) => {
        onSelectProfile(id);
        onToggleProfileManager(false);
    };

    const handleSelectProfileModal = (id: string) => {
        onSelectProfile(id);
        onToggleProfileManager(false);
        setSwitchModalOpen(false);
    };

    const copyInviteUrl = async () => {
        try {
            await navigator.clipboard.writeText(inviteUrl);
            setToastMessage('Enlace copiado al portapapeles');
        } catch {
            try {
                const temp = document.createElement('textarea');
                temp.value = inviteUrl;
                document.body.appendChild(temp);
                temp.select();
                document.execCommand('copy');
                document.body.removeChild(temp);
                setToastMessage('Enlace copiado al portapapeles');
            } catch {
                setToastMessage('No se pudo copiar el enlace');
            }
        }
    };

    const saveGmName = () => {
        const name = gmNameDraft.trim();
        if (!name) {
            setGmNameError('Escribe un nombre para el narrador.');
            return;
        }
        onFormChange({
            ...formData,
            name,
        });
        setStoredGmName(roomId, playerId, name);
        setGmNameError('');
        if (requireGmNameStep) {
            completeGmNameStep(roomId, playerId);
            setRequireGmNameStep(false);
            setShowGmNameModal(false);
            setRequireShareStep(true);
            setShowShareModal(true);
            return;
        }
        setShowGmNameModal(false);
    };

    const closeShareModal = () => {
        if (requireShareStep) {
            completeGmShareStep(roomId, playerId);
            setRequireShareStep(false);
        }
        setShowShareModal(false);
    };

    const saveRoomDiceConfig = async (): Promise<boolean> => {
        setDiceConfigError('');
        try {
            const updated = await updateDiceConfig(roomId, playerId, diceConfigDraft);
            if (!updated) {
                setDiceConfigError('Solo el narrador puede cambiar la configuración de dados.');
                return false;
            }
            setRoomDiceConfig(diceConfigDraft);
            return true;
        } catch (error) {
            console.error('updateDiceConfig failed', error);
            setDiceConfigError('No se pudo actualizar la configuración.');
            return false;
        }
    };

    const saveRoomTheme = async (): Promise<boolean> => {
        setThemeError('');
        try {
            const updated = await updateRoomTheme(roomId, playerId, themeDraft);
            if (!updated) {
                setThemeError('Solo el narrador puede cambiar el tema de sala.');
                return false;
            }
            onRoomThemeChange(themeDraft);
            return true;
        } catch (error) {
            console.error('updateRoomTheme failed', error);
            setThemeError('No se pudo actualizar el tema.');
            return false;
        }
    };

    const showCharacterPicker = !activeProfileId || showProfileManager;
    const isOnlyDice = roomMode === 'only-dice';

    const handleConfirmOnlyDiceName = () => {
        const trimmed = onlyDiceNameDraft.trim();
        if (!trimmed) return;
        onFormChange({ name: trimmed });
        setIsEditingName(false);
    };

    return (
        <div className={`room-workspace ${mobileRoomPanelOpen ? 'panel-open' : ''}`.trim()}>
            <div className="room-topbar">
                <div className="room-topbar-inner">
                    <Logo width={60} className="logo-room" />
                    <div className="room-topbar-actions">
                        <button
                            className="button-link room-topbar-link room-workspace-rules"
                            type="button"
                            onClick={() => setRulesModalOpen(true)}
                            aria-label="Abrir reglas"
                        >
                            <IoBookOutline /> Reglas
                        </button>
                        <div className="room-menu" ref={roomMenuRef}>
                            <button
                                className="button-link room-topbar-link room-menu-trigger"
                                type="button"
                                onClick={() => setRoomMenuOpen(open => !open)}
                            >
                                <span className="room-topbar-room"><IoPeopleOutline /> <strong>Sala:</strong> {roomId}</span>
                                {roomMenuOpen ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
                            </button>
                            {roomMenuOpen ? (
                                <div className="room-menu-dropdown">
                                    {isGm ? (
                                        <>
                                            <div className="room-menu-label">Comparte esta sala</div>
                                            <div className="room-menu-url-wrap">
                                                <div className="room-menu-url">{inviteUrl}</div>
                                                <button
                                                    className="room-menu-copy-icon"
                                                    type="button"
                                                    onClick={() => void copyInviteUrl()}
                                                    aria-label="Copiar enlace"
                                                    title="Copiar enlace"
                                                >
                                                    <IoCopyOutline />
                                                </button>
                                            </div>
                                        </>
                                    ) : null}
                                    <button
                                        className="button-link room-workspace-leave room-menu-leave"
                                        type="button"
                                        onClick={() => {
                                            setRoomMenuOpen(false);
                                            setLeaveConfirmOpen(true);
                                        }}
                                    >
                                        <IoFlashOffOutline /> Salir
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>

            <div className="layout-container room-workspace-layout">
                <section className="room-workspace-main">
                    {isOnlyDice && !isGm ? (
                        <div className="card only-dice-player-card">
                            {!formData.name?.trim() || isEditingName ? (
                                <div className="only-dice-name-entry">
                                    <h3>¿Cómo te llamas?</h3>
                                    <p>Tu nombre aparecerá en el registro de tiradas.</p>
                                    <input
                                        type="text"
                                        placeholder="Tu nombre"
                                        value={onlyDiceNameDraft}
                                        onChange={e => setOnlyDiceNameDraft(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') handleConfirmOnlyDiceName(); }}
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        className="button"
                                        onClick={handleConfirmOnlyDiceName}
                                    >
                                        Entrar a la sala
                                    </button>
                                </div>
                            ) : (
                                <div className="only-dice-ready">
                                    <div className="only-dice-player-name">{formData.name?.trim() || onlyDiceNameDraft}</div>
                                    <p className="muted">Usa el panel de dados para tirar.</p>
                                    <button
                                        type="button"
                                        className="button-link"
                                        onClick={() => {
                                            setOnlyDiceNameDraft(formData.name?.trim() || '');
                                            setIsEditingName(true);
                                        }}
                                    >
                                        Cambiar nombre
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : isGm ? (
                        <div className="card gm-workspace-card">
                            <div className="gm-workspace-tools">
                                <button
                                    className="button-link gm-settings-button"
                                    type="button"
                                    onClick={() => {
                                        originalThemeRef.current = roomTheme;
                                        setThemeDraft(roomTheme);
                                        setThemeError('');
                                        setThemeModalOpen(true);
                                    }}
                                    aria-label="Configurar sala"
                                    title="Configurar sala"
                                >
                                    <IoSettingsOutline />
                                </button>
                            </div>
                            <div className="gm-workspace-header">
                                <div className="gm-workspace-narrador">
                                    <strong>Narrador:</strong> {currentDisplayName}
                                    <span className="gm-workspace-narrador-actions">
                                        <button
                                            className="button-link"
                                            type="button"
                                            onClick={() => {
                                                setGmNameDraft(currentDisplayName);
                                                setGmNameError('');
                                                setRequireGmNameStep(false);
                                                setShowGmNameModal(true);
                                            }}
                                        >
                                            <IoCreateOutline /> Editar
                                        </button>
                                    </span>
                                </div>
                            </div>
                            <h2 className="room-players-heading">Jugadores en sala</h2>
                            {otherPlayers.length === 0 ? (
                                <div className="muted">Todavía no hay jugadores conectados.</div>
                            ) : (
                                <div className="gm-player-list">
                                    {otherPlayers.map(([id, player]) => (
                                        <div key={id} className="gm-player-item">
                                            <div className="gm-player-item-main">
                                                <div className="gm-player-item-title">
                                                    {player?.characterSheet?.name?.trim() || player?.name?.trim() || 'Anónimo'}
                                                </div>
                                                <div className="gm-player-item-meta-row">
                                                    <span><IoFlashOutline /> Brío: {getSheetPreview(player?.characterSheet).brio}</span>
                                                    <span><IoAlertCircleOutline /> Estrés: {getSheetPreview(player?.characterSheet).stress}</span>
                                                    <span><IoMoonOutline /> Sombra: {getSheetPreview(player?.characterSheet).shadow}</span>
                                                    {getSheetPreview(player?.characterSheet).consequences.length > 0 ? (
                                                        <span><IoWarningOutline /> Consecuencias: {getSheetPreview(player?.characterSheet).consequences.length}</span>
                                                    ) : null}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="button-link gm-player-item-action"
                                                onClick={() => setInspectedPlayerId(id)}
                                            >
                                                <IoEyeOutline /> Ver personaje
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : showCharacterPicker ? (
                        <ProfileManager onSelect={handleSelectProfileInline} navigateOnSelect={null} />
                    ) : (
                        <Form
                            formData={formData}
                            onFormChange={onFormChange}
                            nameLabelAction={(
                                <button className="button-link" type="button" onClick={() => setSwitchConfirmOpen(true)}>
                                    Cambiar PJ <IoSwapHorizontalOutline />
                                </button>
                            )}
                        />
                    )}
                </section>
            </div>

            <button
                className="room-panel-toggle"
                type="button"
                onClick={() => setMobileRoomPanelOpen(open => !open)}
                aria-label={mobileRoomPanelOpen ? 'Cerrar panel de sala' : 'Abrir panel de sala'}
                title={mobileRoomPanelOpen ? 'Cerrar panel de sala' : 'Abrir panel de sala'}
            >
                <IoDiceOutline />
                {mobileRoomPanelOpen ? <IoChevronForwardOutline /> : <IoChevronBackOutline />}
            </button>

            <button
                className={`room-panel-backdrop ${mobileRoomPanelOpen ? 'open' : ''}`}
                type="button"
                aria-label="Cerrar panel de sala"
                onClick={() => setMobileRoomPanelOpen(false)}
            />

            <aside className={`room-workspace-side ${mobileRoomPanelOpen ? 'open' : ''}`}>
                <div className="room-players">
                    {Object.keys(players).length === 0 && <div className="muted">No hay jugadores conectados</div>}
                    {Object.entries(players).map(([id, player]) => (
                        <RoomPlayer
                            key={id}
                            name={player?.name ?? 'Anónimo'}
                            subtitle={player?.role === 'gm' ? 'Narrador' : undefined}
                            lastRoll={player?.lastRoll ?? null}
                            dark
                            rollUpdated={Boolean(updatedRollPlayers[id])}
                        />
                    ))}
                </div>
                <div className="room-dice-launcher">
                    <DiceRoller
                        roomId={roomId}
                        playerId={playerId}
                        showInlineResult={false}
                        roomMode={roomMode}
                        diceConfig={roomDiceConfig}
                    />
                </div>
            </aside>

            <ConfirmationModal
                open={leaveConfirmOpen}
                title="¿Salir de la sala?"
                description="Saldrás de la sala a la que te has conectado."
                onConfirm={onLeaveRoom}
                onCancel={() => setLeaveConfirmOpen(false)}
            />

            <ConfirmationModal
                open={switchConfirmOpen}
                title="¿Cambiar personaje?"
                description="Puedes seleccionar otro personaje o crear uno nuevo."
                onConfirm={() => {
                    setSwitchConfirmOpen(false);
                    setSwitchModalOpen(true);
                }}
                onCancel={() => setSwitchConfirmOpen(false)}
            />

            <Modal open={switchModalOpen} onClose={() => setSwitchModalOpen(false)}>
                <ProfileManager onSelect={handleSelectProfileModal} navigateOnSelect={null} renderCard={false} />
            </Modal>

            <Modal open={rulesModalOpen} onClose={() => setRulesModalOpen(false)}>
                <Rules renderCard={false} />
            </Modal>

            <Modal
                open={showGmNameModal}
                onClose={() => {
                    if (requireGmNameStep) return;
                    setShowGmNameModal(false);
                }}
                closeOnOverlayClick={!requireGmNameStep}
                closeOnEscape={!requireGmNameStep}
                showCloseButton={!requireGmNameStep}
            >
                <div className="gm-modal-content">
                    <h3>{requireGmNameStep ? '¿Cómo te llamas como narrador?' : 'Editar nombre del narrador'}</h3>
                    <p>Este nombre te identifica en la sala y en el área de tiradas.</p>
                    <input
                        type="text"
                        value={gmNameDraft}
                        onChange={event => {
                            setGmNameDraft(event.target.value);
                            if (gmNameError) setGmNameError('');
                        }}
                        placeholder="Nombre del narrador"
                        autoFocus
                    />
                    {gmNameError ? <div className="room-selector-error">{gmNameError}</div> : null}
                    <div className="gm-modal-actions">
                        {!requireGmNameStep ? (
                            <button
                                type="button"
                                className="button button-secondary"
                                onClick={() => setShowGmNameModal(false)}
                            >
                                Cancelar
                            </button>
                        ) : null}
                        <button type="button" className="button" onClick={saveGmName}>
                            Guardar
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                open={showShareModal}
                onClose={() => {
                    if (requireShareStep) return;
                    setShowShareModal(false);
                }}
                closeOnOverlayClick={!requireShareStep}
                closeOnEscape={!requireShareStep}
                showCloseButton={!requireShareStep}
            >
                <div className="gm-modal-content">
                    <h3>Comparte esta sala con tus jugadores</h3>
                    <p>Los jugadores solo podrán entrar usando este enlace.</p>
                    <div className="room-menu-url-wrap">
                        <div className="room-menu-url">{inviteUrl}</div>
                        <button
                            className="room-menu-copy-icon"
                            type="button"
                            onClick={() => void copyInviteUrl()}
                            aria-label="Copiar enlace"
                            title="Copiar enlace"
                        >
                            <IoCopyOutline />
                        </button>
                    </div>
                    <div className="gm-modal-actions">
                        <button type="button" className="button" onClick={closeShareModal}>
                            Entendido
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal open={Boolean(inspectedPlayerId)} onClose={() => setInspectedPlayerId(null)} expanded>
                <div className="gm-modal-content">
                    <h3>Personaje: {inspectedSheetPreview.name || inspectedPlayer?.name?.trim() || 'Anónimo'}</h3>
                    <p className="gm-modal-note">Vista de la hoja de personaje. Solo el jugador puede hacer cambios.</p>

                    {inspectedPlayer?.characterSheet ? (
                        <div className="gm-sheet-preview">
                            <div className="gm-sheet-section gm-sheet-section-character">
                                <h4>PERSONAJE</h4>
                                <div>
                                    <label>Nombre</label>
                                    <input type="text" readOnly value={inspectedSheetPreview.name} />
                                </div>
                                <div>
                                    <label>Profesión</label>
                                    <input type="text" readOnly value={inspectedSheetPreview.occupation} />
                                </div>
                                <div>
                                    <label>Descripción</label>
                                    <textarea readOnly rows={4} value={inspectedSheetPreview.description} />
                                </div>
                            </div>

                            <div className="gm-sheet-section gm-sheet-section-traits">
                                <h4>CARACTERÍSTICAS</h4>
                                <div className="gm-sheet-stats-grid">
                                    <div className="gm-sheet-field-inline"><label>Agilidad</label><input type="text" readOnly value={inspectedSheetPreview.agility} /></div>
                                    <div className="gm-sheet-field-inline"><label>Fuerza</label><input type="text" readOnly value={inspectedSheetPreview.strength} /></div>
                                    <div className="gm-sheet-field-inline"><label>Destreza</label><input type="text" readOnly value={inspectedSheetPreview.dexterity} /></div>
                                    <div className="gm-sheet-field-inline"><label>Cultura</label><input type="text" readOnly value={inspectedSheetPreview.culture} /></div>
                                    <div className="gm-sheet-field-inline"><label>Oficio</label><input type="text" readOnly value={inspectedSheetPreview.craft} /></div>
                                    <div className="gm-sheet-field-inline"><label>Templanza</label><input type="text" readOnly value={inspectedSheetPreview.temperance} /></div>
                                    <div className="gm-sheet-field-inline"><label>Perspicacia</label><input type="text" readOnly value={inspectedSheetPreview.insight} /></div>
                                </div>
                            </div>

                            <div className="gm-sheet-section gm-sheet-section-status">
                                <h4>ESTADO</h4>
                                <div className="gm-sheet-stats-grid gm-sheet-status-grid">
                                    <div className="gm-sheet-field-inline"><label><IoFlashOutline /> Brío</label><input type="text" readOnly value={inspectedSheetPreview.brio} /></div>
                                    <div className="gm-sheet-field-inline"><label><IoAlertCircleOutline /> Estrés</label><input type="text" readOnly value={inspectedSheetPreview.stress} /></div>
                                    <div className="gm-sheet-field-inline"><label><IoMoonOutline /> Sombra</label><input type="text" readOnly value={inspectedSheetPreview.shadow} /></div>
                                </div>
                            </div>

                            <div className="gm-sheet-section">
                                <h4><IoWarningOutline /> Consecuencias</h4>
                                <div>
                                    {inspectedSheetPreview.consequences.length > 0 ? (
                                        <ul className="gm-sheet-consequence-list">
                                            {inspectedSheetPreview.consequences.map(consequence => (
                                                <li key={consequence.id}>
                                                    {`Consecuencia ${consequence.type}: ${consequence.description || 'sin descripción'} (${CONSEQUENCE_TYPE_LABELS[consequence.type]})`}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <input type="text" readOnly value="Sin consecuencias por ahora" />
                                    )}
                                </div>
                            </div>

                            <div className="gm-sheet-section gm-sheet-section-notes">
                                <h4>NOTAS</h4>
                                <textarea readOnly rows={4} value={inspectedSheetPreview.notes} />
                            </div>
                        </div>
                    ) : (
                        <div className="muted">Este jugador aún no tiene una ficha activa.</div>
                    )}
                </div>
            </Modal>

            <Modal
                open={themeModalOpen}
                onClose={() => {
                    onRoomThemeChange(originalThemeRef.current);
                    setThemeModalOpen(false);
                }}
            >
                <div className="gm-modal-content">
                    <h3>Configuración de sala</h3>
                    <p>Puedes cambiar la apariencia visual de la sala con uno de los temas disponibles.</p>
                    <label htmlFor="room-theme-select">Tema de la sala</label>
                    <select
                        id="room-theme-select"
                        value={themeDraft}
                        onChange={event => {
                            const theme = event.target.value as RoomTheme;
                            setThemeDraft(theme);
                            onRoomThemeChange(theme);
                        }}
                    >
                        {ROOM_THEME_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    {themeError ? <div className="room-selector-error">{themeError}</div> : null}

                    {isOnlyDice ? (
                        <>
                            <div className="gm-modal-divider" />
                            <label className="gm-modal-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={diceConfigDraft.exploding}
                                    onChange={e => setDiceConfigDraft(prev => ({ ...prev, exploding: e.target.checked }))}
                                />
                                Dados explosivos (el dado máximo se vuelve a tirar)
                            </label>
                            {diceConfigError ? <div className="room-selector-error">{diceConfigError}</div> : null}
                        </>
                    ) : null}

                    <div className="gm-modal-actions">
                        <button
                            type="button"
                            className="button button-secondary"
                            onClick={() => {
                                onRoomThemeChange(originalThemeRef.current);
                                setDiceConfigDraft(roomDiceConfig);
                                setThemeModalOpen(false);
                            }}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="button"
                            onClick={async () => {
                                const themeOk = await saveRoomTheme();
                                if (!themeOk) return;
                                if (isOnlyDice) {
                                    const configOk = await saveRoomDiceConfig();
                                    if (!configOk) return;
                                }
                                setThemeModalOpen(false);
                            }}
                        >
                            Guardar
                        </button>
                    </div>
                </div>
            </Modal>

            <Toast
                open={Boolean(toastMessage)}
                message={toastMessage}
                onClose={() => setToastMessage('')}
                className="room-toast"
            />
        </div>
    );
};

export default RoomWorkspace;
